import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { SBA_CONFIG } from "@/pages/assessment/config";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Loader2, ShieldCheck, AlertTriangle, Info, CheckCircle2,
  Compass, HeartHandshake, PhoneCall, ClipboardCheck, Stethoscope, Pill,
  ArrowRight, Lock, Umbrella,
} from "lucide-react";

const SEVERITY = {
  attention: { icon: AlertTriangle, ring: "border-[#E9C46A] bg-[#FBF6E8]", dot: "text-[#B45309]", tag: "Worth a closer look" },
  info: { icon: Info, ring: "border-[#BCCCDC] bg-white", dot: "text-[#1E3E62]", tag: "May be worth reviewing" },
};
const PRIORITY_BADGE = {
  high: "bg-[#0B192C] text-white", medium: "bg-[#1E3E62] text-white", low: "bg-[#E5E5E0] text-[#0B192C]",
};
const NBA_ICON = {
  talk_to_advisor: PhoneCall, compare_rx: Pill, verify_providers: Stethoscope, review_on_own: Compass,
};

export default function SBAAssessmentResults({ config = SBA_CONFIG }) {
  const [state, setState] = useState({ loading: true });
  const [ai, setAi] = useState(null);
  const [reviewStatus, setReviewStatus] = useState(null); // null | 'requested' | 'error'
  const [lifelink, setLifelink] = useState(null); // null | {loading} | {resolved,...} | {error}
  const [mcFulfill, setMcFulfill] = useState(null); // medicare explore result
  const token = new URLSearchParams(window.location.search).get("resume");

  const exploreMedicare = async () => {
    setMcFulfill({ loading: true });
    try {
      const { data } = await api.post(`/public/assessment/${token}/medicare-fulfillment`);
      if (data?.resolved && data?.destination?.url) {
        setMcFulfill(data);
        window.open(data.destination.url, "_blank", "noopener");
      } else {
        setMcFulfill({ unresolved: true });
      }
    } catch (_) {
      setMcFulfill({ error: true });
    }
  };

  const requestReview = async (body) => {
    try {
      const { data } = await api.post(`/public/assessment/${token}/handoff`, body || {});
      setReviewStatus(data?.ok ? "requested" : "error");
    } catch (_) {
      setReviewStatus("error");
    }
  };

  const continueToLifeLink = async () => {
    setLifelink({ loading: true });
    try {
      const { data } = await api.post(`/public/assessment/${token}/enrollment-destination`);
      if (data?.resolved && data?.destination?.url) {
        setLifelink(data);
        window.open(data.destination.url, "_blank", "noopener");
      } else {
        setLifelink({ error: true });
      }
    } catch (_) {
      setLifelink({ error: true });
    }
  };

  useEffect(() => {
    (async () => {
      if (!token) { setState({ loading: false, error: "missing" }); return; }
      try {
        const { data } = await api.get(`/public/assessment/${token}/results`);
        setState({ loading: false, data });
        // AI overlay is best-effort and never blocks the deterministic Results.
        if (data?.ready) {
          try {
            const { data: n } = await api.post(`/public/assessment/${token}/narrative`);
            if (n?.narrative) setAi(n);
          } catch (_) { /* silent: deterministic results already shown */ }
        }
      } catch (e) {
        const code = e?.response?.status;
        setState({ loading: false, error: code === 404 ? "notfound" : "generic" });
      }
    })();
  }, [token]);

  if (state.loading) {
    return <Shell><div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#1E3E62]" /></div></Shell>;
  }

  if (state.error || !state.data) {
    const msg = state.error === "missing" || state.error === "notfound"
      ? "We couldn't find those results. The link may be incomplete or expired."
      : "Something went wrong loading your results. Please try again shortly.";
    return (
      <Shell config={config}>
        <div className="max-w-lg mx-auto text-center py-24" data-testid="results-error">
          <p className="text-lg text-[#4B5563]">{msg}</p>
          <Button asChild className="mt-6 h-12 px-6 rounded-full bg-[#0B192C] text-white" data-testid="results-restart-btn"><Link to={config.assessmentPath}>Start the assessment</Link></Button>
        </div>
      </Shell>
    );
  }

  const d = state.data;
  if (!d.ready) {
    return (
      <Shell config={config}>
        <div className="max-w-lg mx-auto text-center py-24" data-testid="results-not-ready">
          <h1 className="font-display text-2xl font-bold text-[#0B192C]">Let's finish your assessment</h1>
          <p className="mt-3 text-[#4B5563] text-lg">It looks like a few questions are still left. Pick up right where you left off.</p>
          <Button asChild className="mt-6 h-12 px-6 rounded-full bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620]" data-testid="results-resume-btn">
            <Link to={`${config.assessmentPath}?resume=${encodeURIComponent(token)}`}>Continue my assessment</Link>
          </Button>
        </div>
      </Shell>
    );
  }

  const intel = d.intelligence || {};
  const gaps = intel.gaps || [];
  const recs = intel.recommendations || [];
  const priorities = d.current_picture?.priorities || [];
  const pictureItems = d.current_picture?.items || [];
  const nba = intel.next_best_action || {};
  const NbaIcon = NBA_ICON[nba.code] || Compass;
  const firstName = d.first_name && d.first_name !== "Friend" ? d.first_name : null;

  return (
    <Shell config={config}>
      <div className="max-w-3xl mx-auto py-8 sm:py-12" data-testid="results-page">
        {/* 1. Completion moment */}
        <span className="inline-flex items-center gap-2 text-[#2D6A4F] text-sm font-bold uppercase tracking-wide" data-testid="results-based-on">
          <Sparkles className="w-4 h-4" /> Based on your answers
        </span>
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0B192C] mt-3 leading-[1.12]" data-testid="results-heading">
          {firstName ? `${firstName}, your ${config.intelligenceName} is ready.` : `Your ${config.intelligenceName} is ready.`}
        </h1>
        <p className="mt-4 text-lg text-[#4B5563] leading-relaxed">
          {config.resultsSubtitle}
        </p>

        {/* Optional AI overlay: plain-English summary of the deterministic result */}
        {ai?.narrative && (
          <div className="mt-6 rounded-2xl border-2 border-[#BCCCDC] bg-white p-6" data-testid="results-ai-narrative">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-[#E9C46A]" />
              <h2 className="font-display font-bold text-lg text-[#0B192C]">In plain English</h2>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7280] ml-auto">AI summary</span>
            </div>
            <p className="text-base text-[#374151] leading-relaxed" data-testid="results-ai-narrative-text">{ai.narrative}</p>
          </div>
        )}

        {/* 2. Current picture */}
        {(pictureItems.length > 0 || priorities.length > 0) && (
          <Card testid="results-current-picture" title={config.pictureTitle} icon={ClipboardCheck}>
            {pictureItems.length > 0 && (
              <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {pictureItems.map((it) => (
                  <div key={it.label} data-testid={`picture-item-${it.label}`}>
                    <dt className="text-sm font-semibold text-[#6B7280]">{it.label}</dt>
                    <dd className="text-lg font-semibold text-[#0B192C]">{it.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Card>
        )}

        {/* Attention (gaps), Opportunities (recs), and Priorities — ordered per journey.
            Life consolidates: priorities first, then a single synthesized attention list,
            and hides the (repetitive) opportunities cards. */}
        {(() => {
          const prioritiesBlock = priorities.length > 0 ? (
            <React.Fragment key="priorities">
              <SectionTitle>Here's what matters most to you</SectionTitle>
              <div className="flex flex-wrap gap-2.5" data-testid="results-priorities">
                {priorities.map((p) => (
                  <span key={p} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#FAF6E8] border-2 border-[#E9C46A] text-[#0B192C] font-semibold text-base" data-testid={`priority-${p}`}>
                    <CheckCircle2 className="w-4 h-4 text-[#2D6A4F]" /> {p}
                  </span>
                ))}
              </div>
            </React.Fragment>
          ) : null;

          const attentionBlock = (
            <React.Fragment key="attention">
              <SectionTitle>{config.attentionTitle}</SectionTitle>
              {gaps.length === 0 ? (
                <div className="rounded-2xl border-2 border-[#CDE7D8] bg-[#F0F7F4] p-5 flex items-start gap-3" data-testid="results-gaps-empty">
                  <CheckCircle2 className="w-6 h-6 text-[#2D6A4F] shrink-0" />
                  <p className="text-[#1F3D2F] text-base">Nothing significant was flagged from your answers. It can still be worth a periodic review as your needs change.</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="results-gaps">
                  {gaps.map((g) => {
                    const sv = SEVERITY[g.severity] || SEVERITY.info;
                    const Icon = sv.icon;
                    return (
                      <div key={g.code} className={`rounded-2xl border-2 ${sv.ring} p-5`} data-testid={`gap-${g.code}`}>
                        <div className="flex items-start gap-3">
                          <Icon className={`w-6 h-6 shrink-0 ${sv.dot}`} />
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-display font-bold text-lg text-[#0B192C]">{g.label}</h3>
                              <span className="text-xs font-semibold text-[#6B7280]">{sv.tag}</span>
                            </div>
                            <p className="mt-1 text-base text-[#4B5563] leading-relaxed">{g.rationale}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );

          const recsBlock = (config.showRecommendations && recs.length > 0) ? (
            <React.Fragment key="recs">
              <SectionTitle>Potential opportunities</SectionTitle>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="results-recommendations">
                {recs.map((r) => (
                  <div key={r.category} className="rounded-2xl border-2 border-[#E5E5E0] bg-white p-5" data-testid={`rec-${r.category}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${PRIORITY_BADGE[r.priority] || PRIORITY_BADGE.low}`}>{r.priority}</span>
                    </div>
                    <h3 className="font-display font-bold text-lg text-[#0B192C] mt-3">{r.label}</h3>
                    <p className="mt-1 text-base text-[#4B5563] leading-relaxed">{r.why}</p>
                  </div>
                ))}
              </div>
            </React.Fragment>
          ) : null;

          return config.prioritiesFirst
            ? [prioritiesBlock, attentionBlock, recsBlock]
            : [attentionBlock, recsBlock, prioritiesBlock];
        })()}

        {/* 6. Your next step */}
        {nba.label && (
          <div className="mt-10 rounded-2xl bg-[#0B192C] text-white p-6 sm:p-7" data-testid="results-next-step">
            <span className="text-[#E9C46A] text-sm font-bold uppercase tracking-wide">Your next step</span>
            <div className="flex items-start gap-3 mt-3">
              <NbaIcon className="w-7 h-7 text-[#E9C46A] shrink-0" />
              <p className="text-xl font-display font-semibold leading-snug">
                Based on what you told us, {nba.label.toLowerCase()} may be a helpful next step.
              </p>
            </div>
          </div>
        )}

        {/* 7. How to continue — journey aware */}
        <SectionTitle>How would you like to continue?</SectionTitle>
        {config.journeyType === "life" ? (
          <LifePaths nba={nba} requestReview={requestReview} reviewStatus={reviewStatus}
                     lifelink={lifelink} continueToLifeLink={continueToLifeLink} />
        ) : (
          <div className="grid gap-4" data-testid="results-choices">
            <div className="rounded-2xl border-2 border-[#D4AF37] bg-[#FAF6E8] p-6" data-testid="results-explore-medicare">
              <h3 className="font-display font-bold text-lg text-[#0B192C]">Explore Medicare plans</h3>
              <p className="mt-1 text-base text-[#4B5563]">Browse available Medicare options through our licensed enrollment platform. You can look on your own — no agent required.</p>
              <Button onClick={exploreMedicare} disabled={mcFulfill?.loading}
                className="mt-4 h-12 px-6 rounded-full bg-[#0B192C] text-white hover:bg-[#1E3E62] font-bold" data-testid="results-explore-medicare-btn">
                {mcFulfill?.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Explore Medicare plans <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
              {mcFulfill?.resolved && <p className="mt-3 text-sm text-[#6B7280]" data-testid="results-medicare-note">Opening the enrollment platform in a new tab.</p>}
              {mcFulfill?.unresolved && (
                <div className="mt-4 rounded-xl border-2 border-[#BCCCDC] bg-white p-4" data-testid="results-medicare-unresolved">
                  <p className="text-base text-[#0B192C] font-semibold">Online plan browsing isn't available for your area yet.</p>
                  <p className="mt-1 text-sm text-[#4B5563]">We won't show plans we can't confirm for your location and plan year — no made-up options. A licensed insurance agent can walk you through the plans that actually apply to you, at no cost and no obligation.</p>
                  <Button onClick={() => requestReview()} disabled={reviewStatus === "requested"}
                    className="mt-3 h-11 px-5 rounded-full bg-[#0B192C] text-white hover:bg-[#1E3E62] font-bold" data-testid="results-medicare-unresolved-advisor">
                    {reviewStatus === "requested" ? <>Request sent <CheckCircle2 className="w-4 h-4 ml-1" /></> : <>Have a licensed insurance agent help <ArrowRight className="w-4 h-4 ml-1" /></>}
                  </Button>
                </div>
              )}
              {mcFulfill?.error && <p className="mt-3 text-sm text-[#B45309]">We couldn't open the platform just now. Please try again shortly.</p>}
            </div>
            <Choice testid="results-choice-review" icon={PhoneCall} title="Get help from a licensed insurance agent"
              body="Prefer to talk it through? A licensed insurance agent can review your results and help you enroll — no obligation." onSelect={requestReview} />
          </div>
        )}
        {config.journeyType !== "life" && reviewStatus === "requested" && (
          <div className="mt-4 rounded-2xl border-2 border-[#CDE7D8] bg-[#F0F7F4] p-5 flex items-start gap-3" data-testid="results-review-confirmed">
            <CheckCircle2 className="w-6 h-6 text-[#2D6A4F] shrink-0" />
            <p className="text-[#1F3D2F] text-base">A licensed insurance agent will review your results. Create your free account below so they can reach you.</p>
          </div>
        )}
        {reviewStatus === "error" && (
          <p className="mt-3 text-sm text-[#B45309]" data-testid="results-review-error">We couldn't send that request just now. Please try again in a moment.</p>
        )}

        {/* Explore plans — Benefits Overview sits downstream of Results, offered
            explicitly once the member has an account (never auto-redirected here). */}
        {config.showExplorePlans && d.claimed && (
          <div className="mt-6 rounded-2xl border-2 border-[#E5E5E0] bg-white p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="results-explore-plans">
            <div className="flex-1">
              <h3 className="font-display font-bold text-lg text-[#0B192C]">Ready to look at specific plans?</h3>
              <p className="mt-1 text-base text-[#4B5563]">Browse and compare benefits options in your member dashboard whenever you're ready.</p>
            </div>
            <Button asChild className="h-12 px-6 text-base font-bold rounded-full bg-[#0B192C] text-white hover:bg-[#1E3E62]" data-testid="results-explore-plans-btn">
              <Link to="/app/benefits">Explore plans <ArrowRight className="w-4 h-4 ml-1" /></Link>
            </Button>
          </div>
        )}

        {/* 8. Save your results */}
        {!d.claimed && (
          <div className="mt-10 rounded-2xl border-2 border-[#E9C46A] bg-[#FAF6E8] p-6 sm:p-7" data-testid="results-save-cta">
            <div className="flex items-start gap-3">
              <Lock className="w-6 h-6 text-[#B89620] shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-display font-bold text-xl text-[#0B192C]">{config.savePrompt}</h3>
                <p className="mt-1 text-base text-[#4B5563]">{config.saveSub}</p>
                <Button asChild className="mt-4 h-12 px-6 text-base font-bold rounded-full bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620]" data-testid="results-create-account">
                  <Link to={`/register?resume=${encodeURIComponent(token)}${config.journeyType === "life" ? "&journey=life" : ""}`}>
                    Create my free account <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* 9. Disclaimer / trust footer */}
        <div className="mt-10 border-t border-[#E5E5E0] pt-6" data-testid="results-disclaimer">
          <p className="text-sm text-[#6B7280] leading-relaxed flex gap-2">
            <ShieldCheck className="w-4 h-4 text-[#2D6A4F] shrink-0 mt-0.5" />
            <span>
              {intel.disclaimer}{" "}
              These results are based on the information you provided and highlight areas that may be worth reviewing —
              they are not a plan recommendation, a suitability or underwriting determination, or a guarantee of eligibility.
            </span>
          </p>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children, config = SBA_CONFIG }) {
  const Logo = config.Logo;
  return (
    <div className="min-h-screen bg-[#FBFBF9] text-[#111827]">
      <header className="sticky top-0 z-30 bg-[#FBFBF9]/90 backdrop-blur border-b border-[#E5E5E0]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
          <Link to={config.homePath} aria-label={config.logoAria}><Logo className={config.logoClass} /></Link>
          <span className="hidden sm:inline-flex items-center gap-2 text-[#1E3E62] font-semibold text-sm">
            <Sparkles className="w-4 h-4 text-[#E9C46A]" /> {config.resultsTag}
          </span>
        </div>
      </header>
      <main className="px-4 sm:px-8">{children}</main>
    </div>
  );
}

function LifePaths({ nba, requestReview, reviewStatus, lifelink, continueToLifeLink }) {
  const isTerm = nba?.code === "explore_term";
  return (
    <div className="grid gap-4" data-testid="results-life-paths">
      {isTerm && (
        <div className="rounded-2xl border-2 border-[#D4AF37] bg-[#FAF6E8] p-6" data-testid="life-path-term">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-full bg-[#D4AF37] text-[#0B192C] flex items-center justify-center shrink-0"><Umbrella className="w-5 h-5" /></div>
            <div className="flex-1">
              <h3 className="font-display font-bold text-lg text-[#0B192C]">Continue to LifeLink</h3>
              <p className="mt-1 text-base text-[#4B5563]">Based on your answers, term coverage may be worth exploring. Continue to LifeLink to review available term options and take the next step.</p>
              <Button onClick={continueToLifeLink} disabled={lifelink?.loading}
                className="mt-4 h-12 px-6 rounded-full bg-[#0B192C] text-white hover:bg-[#1E3E62] font-bold" data-testid="life-continue-lifelink-btn">
                {lifelink?.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue to LifeLink <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
              {lifelink?.resolved && (
                <p className="mt-3 text-sm text-[#6B7280]" data-testid="life-lifelink-note">Opening LifeLink in a new tab. <span className="font-semibold">Demo placeholder</span> — not a live Simplicity service yet.</p>
              )}
              {lifelink?.error && <p className="mt-3 text-sm text-[#B45309]" data-testid="life-lifelink-error">We couldn't open LifeLink just now. Please try again shortly.</p>}
            </div>
          </div>
        </div>
      )}
      <AdvisorRequestCard requestReview={requestReview} reviewStatus={reviewStatus}
        title={isTerm ? "Prefer to talk with a licensed insurance agent?" : "Have a licensed insurance agent review this"}
        body={isTerm
          ? "A licensed insurance agent can review your results with you — no obligation. Share how to reach you and we'll have someone follow up."
          : "Your answers point to choices that are usually best made with a licensed insurance agent. Share how to reach you and we'll have someone follow up — no obligation."} />
    </div>
  );
}

function AdvisorRequestCard({ requestReview, reviewStatus, title, body }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const canSend = (email.trim() || phone.trim()) && consent && reviewStatus !== "requested";

  if (reviewStatus === "requested") {
    return (
      <div className="rounded-2xl border-2 border-[#CDE7D8] bg-[#F0F7F4] p-6 flex items-start gap-3" data-testid="results-review-confirmed">
        <CheckCircle2 className="w-6 h-6 text-[#2D6A4F] shrink-0" />
        <p className="text-[#1F3D2F] text-base">Thanks — a licensed insurance agent will review your results and reach out using the contact details you shared.</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border-2 border-[#BCCCDC] bg-white p-6" data-testid="life-path-advisor">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-[#EEF2F6] text-[#1E3E62] flex items-center justify-center shrink-0"><PhoneCall className="w-5 h-5" /></div>
        <div className="flex-1">
          <h3 className="font-display font-bold text-lg text-[#0B192C]">{title}</h3>
          <p className="mt-1 text-base text-[#4B5563]">{body}</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              className="h-12 px-4 rounded-xl border-2 border-[#E5E5E0] bg-white text-base focus:outline-none focus:border-[#1E3E62]" data-testid="life-advisor-email" />
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone"
              className="h-12 px-4 rounded-xl border-2 border-[#E5E5E0] bg-white text-base focus:outline-none focus:border-[#1E3E62]" data-testid="life-advisor-phone" />
          </div>
          <label className="mt-3 flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" data-testid="life-advisor-consent" />
            <span className="text-sm text-[#4B5563] leading-relaxed">I agree that a licensed insurance agent may contact me by phone, text, or email about my life insurance review. Consent is not a condition of purchase and I can opt out anytime.</span>
          </label>
          <Button onClick={() => requestReview({ contact: { email: email.trim() || null, phone: phone.trim() || null }, consent: true })}
            disabled={!canSend}
            className="mt-4 h-12 px-6 rounded-full bg-[#0B192C] text-white hover:bg-[#1E3E62] font-bold disabled:opacity-50" data-testid="life-request-advisor-btn">
            Request an agent review <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          {reviewStatus === "error" && <p className="mt-3 text-sm text-[#B45309]" data-testid="life-advisor-error">We couldn't send that just now. Please try again shortly.</p>}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className="font-display font-bold text-2xl text-[#0B192C] tracking-tight mt-10 mb-4">{children}</h2>;
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="mt-8 rounded-2xl border-2 border-[#E5E5E0] bg-white p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-5 h-5 text-[#1E3E62]" />}
        <h2 className="font-display font-bold text-xl text-[#0B192C]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Choice({ icon: Icon, title, body, testid, onSelect }) {
  const [picked, setPicked] = useState(false);
  const toggle = () => {
    const next = !picked;
    setPicked(next);
    if (next && onSelect) onSelect();
  };
  return (
    <button type="button" onClick={toggle} data-testid={testid} aria-pressed={picked}
      className={`text-left w-full rounded-2xl border-2 p-5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3E62] ${picked ? "border-[#D4AF37] bg-[#FAF6E8]" : "border-[#E5E5E0] bg-white hover:border-[#BCCCDC]"}`}>
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${picked ? "bg-[#D4AF37] text-[#0B192C]" : "bg-[#EEF2F6] text-[#1E3E62]"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-bold text-lg text-[#0B192C]">{title}</h3>
          <p className="mt-1 text-base text-[#4B5563] leading-relaxed">{body}</p>
          {picked && (
            <p className="mt-3 text-sm text-[#2D6A4F] font-semibold inline-flex items-center gap-1" data-testid={`${testid}-selected`}>
              <CheckCircle2 className="w-4 h-4" /> Noted — create a free account below so we can keep this with your results.
            </p>
          )}
        </div>
      </div>
    </button>
  );
}
